import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowRight,
  CalendarClock,
  CheckCircle2,
  Clock3,
  RefreshCw,
  RotateCcw,
  Settings2,
  ShieldAlert,
  TimerOff,
  UserRoundX,
  X,
} from 'lucide-react';

import ModalShell from '../components/ModalShell';
import ProductionDashboard from '../components/ProductionDashboard';
import { API_URL } from '../config';
import useModalBackButton from '../hooks/useModalBackButton';
import useToast from '../hooks/useToast';
import { safeFetch } from '../utils/safeFetch';
import { STAGES_CONFIG } from '../utils/stages';
import { formatTimezoneDate } from '../utils/timezone';
import { haptic } from '../utils/vibration';

const RISK_FILTERS = [
  { code: 'all', label: 'Все', icon: ShieldAlert },
  { code: 'deferred', label: 'Отложенные', icon: Clock3 },
  { code: 'today_unassigned', label: 'Без исполнителя', icon: UserRoundX },
  { code: 'today_overdue', label: 'Просрочены', icon: TimerOff },
  { code: 'stage_stale', label: 'Застряли', icon: Clock3 },
  { code: 'order_old', label: 'Долгие', icon: CalendarClock },
  { code: 'active_rework', label: 'Доработки', icon: RotateCcw },
  { code: 'active_revision', label: 'Повторы', icon: RefreshCw },
];

const RISK_DESCRIPTIONS = {
  deferred: 'Заказы, которые исполнитель вернул диспетчеру',
  today_unassigned: 'Выезд сегодня, но исполнитель не выбран',
  today_overdue: 'Закончился назначенный интервал времени',
  stage_stale: 'Заказ дольше заданного срока на текущем этапе',
  order_old: 'Слишком большой общий срок заказа или перестирки',
  active_rework: 'Открытая доработка позиций заказа',
  active_revision: 'Открытый повтор заказа',
};

const RISK_STAGE_CODES = [
  'pickup',
  'washing',
  'drying_packing',
  'approval',
  'delivery',
  'cleaning',
  'handover',
];

const RISK_META = {
  deferred: {
    icon: Clock3,
    className: 'border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-900/60 dark:bg-amber-950/30 dark:text-amber-200',
  },
  today_unassigned: {
    icon: UserRoundX,
    className: 'border-orange-200 bg-orange-50 text-orange-800 dark:border-orange-900/60 dark:bg-orange-950/30 dark:text-orange-200',
  },
  today_overdue: {
    icon: TimerOff,
    className: 'border-red-200 bg-red-50 text-red-800 dark:border-red-900/60 dark:bg-red-950/30 dark:text-red-200',
  },
  stage_stale: {
    icon: Clock3,
    className: 'border-rose-200 bg-rose-50 text-rose-800 dark:border-rose-900/60 dark:bg-rose-950/30 dark:text-rose-200',
  },
  order_old: {
    icon: CalendarClock,
    className: 'border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-900/60 dark:bg-amber-950/30 dark:text-amber-200',
  },
  active_rework: {
    icon: RotateCcw,
    className: 'border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-900/60 dark:bg-amber-950/30 dark:text-amber-200',
  },
  active_revision: {
    icon: RefreshCw,
    className: 'border-violet-200 bg-violet-50 text-violet-800 dark:border-violet-900/60 dark:bg-violet-950/30 dark:text-violet-200',
  },
};

const RISK_FILTER_STORAGE_KEY = 'risk_control_active_filter';

const cloneRiskSettings = (value) => (
  value ? JSON.parse(JSON.stringify(value)) : null
);

const readSavedRiskFilter = () => {
  try {
    const savedFilter = sessionStorage.getItem(RISK_FILTER_STORAGE_KEY) || 'all';
    return RISK_FILTERS.some(({ code }) => code === savedFilter) ? savedFilter : 'all';
  } catch {
    return 'all';
  }
};

const SEVERITY_CARD_CLASS = {
  critical: 'border-red-300 dark:border-red-900/80',
  danger: 'border-orange-200 dark:border-orange-900/60',
  warning: 'border-amber-200 dark:border-amber-900/50',
};

const formatUpdatedAt = (value) => {
  const formatted = formatTimezoneDate(value, true);
  return formatted ? formatted.slice(0, 16) : '';
};

const hasReason = (item, code) => (
  Array.isArray(item?.reasons) && item.reasons.some((reason) => reason.code === code)
);

function MetricCard({ icon: Icon, label, value, tone = 'slate' }) {
  const tones = {
    slate: 'border-gray-200 bg-white text-gray-800 dark:border-gray-800 dark:bg-gray-900 dark:text-gray-100',
    danger: 'border-red-200 bg-red-50 text-red-800 dark:border-red-900/60 dark:bg-red-950/30 dark:text-red-200',
    orange: 'border-orange-200 bg-orange-50 text-orange-800 dark:border-orange-900/60 dark:bg-orange-950/30 dark:text-orange-200',
    blue: 'border-blue-200 bg-blue-50 text-blue-800 dark:border-blue-900/60 dark:bg-blue-950/30 dark:text-blue-200',
  };

  return (
    <div className={`min-w-0 overflow-hidden rounded-xl border px-2.5 py-2.5 ${tones[tone] || tones.slate}`}>
      <div className="flex min-w-0 items-center justify-between gap-1.5">
        <div className="min-w-0 flex-1 overflow-hidden">
          <div className="block overflow-hidden text-ellipsis whitespace-nowrap text-[10px] font-semibold uppercase tracking-wide opacity-70">{label}</div>
          <div className="mt-1 text-xl font-semibold leading-none">{value}</div>
        </div>
        <Icon className="h-4 w-4 shrink-0 opacity-75" />
      </div>
    </div>
  );
}

const formatAgeHours = (value) => {
  const hours = Math.max(0, Number(value || 0));
  const days = Math.floor(hours / 24);
  const remainingHours = hours % 24;
  if (days && remainingHours) return `${days} д ${remainingHours} ч`;
  if (days) return `${days} д`;
  return `${remainingHours} ч`;
};

const getCompactReasonText = (reason, item) => {
  if (reason.code === 'deferred') return 'Отложено';
  if (reason.code === 'today_overdue') return `Просрочено${item.time_slot ? ` · ${item.time_slot}` : ''}`;
  if (reason.code === 'stage_stale') return `На этапе ${formatAgeHours(reason.age_hours)}`;
  if (reason.code === 'order_old') {
    return `${reason.label.startsWith('Перестирка') ? 'Повтору' : 'Заказу'} ${formatAgeHours(reason.age_hours)}`;
  }
  if (reason.code === 'active_rework') return 'Доработка';
  if (reason.code === 'active_revision') return 'Повтор';
  return reason.label;
};

function RiskBadge({ reason, item }) {
  const meta = RISK_META[reason.code] || RISK_META.order_old;
  const Icon = meta.icon;

  return (
    <span className={`inline-flex min-w-0 items-center gap-1 rounded-md border px-1.5 py-1 text-[10px] font-semibold leading-none ${meta.className}`}>
      <Icon className="h-3 w-3 shrink-0" />
      <span className="whitespace-nowrap">{getCompactReasonText(reason, item)}</span>
    </span>
  );
}

function RiskOrderCard({ item, onOpen }) {
  const scheduleText = item.scheduled_date
    ? `${formatTimezoneDate(item.scheduled_date, false)}${item.time_slot ? ` · ${item.time_slot}` : ''}`
    : '';
  const revisionReason = item.reasons.find((reason) => reason.code === 'active_revision');
  const ageDateText = revisionReason?.occurred_at
    ? `Повтор: ${formatTimezoneDate(revisionReason.occurred_at, false)}`
    : `Создан: ${formatTimezoneDate(item.created_date, false)}`;

  return (
    <button
      type="button"
      onClick={() => onOpen(item)}
      className={`group w-full rounded-xl border bg-white p-2.5 text-left shadow-sm transition hover:shadow-md active:bg-gray-50 dark:bg-gray-900 dark:active:bg-gray-800 ${SEVERITY_CARD_CLASS[item.severity] || SEVERITY_CARD_CLASS.warning}`}
    >
      <div className="flex min-w-0 items-center gap-2">
        <span className="shrink-0 rounded-md bg-gray-100 px-1.5 py-1 text-[11px] font-bold text-gray-900 dark:bg-gray-800 dark:text-gray-100">
          № {item.order_number}
        </span>
        <span className="min-w-0 flex-1 truncate text-sm font-semibold text-gray-950 dark:text-white">
          {item.client_name || 'Клиент не указан'}
        </span>
        <ArrowRight className="h-4 w-4 shrink-0 text-gray-400 transition group-hover:text-blue-500" />
      </div>

      <div className="mt-1.5 flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-[10px] leading-4 text-gray-500 dark:text-gray-400">
        <span className="font-semibold text-blue-700 dark:text-blue-300">{item.stage_label}</span>
        <span className="max-w-full truncate">{item.assignee_name || 'Без исполнителя'}</span>
        <span>{ageDateText}</span>
        {scheduleText ? <span>График: {scheduleText}</span> : null}
      </div>

      <div className="mt-2 flex flex-wrap gap-1">
        {item.reasons.map((reason) => (
          <RiskBadge key={reason.code} reason={reason} item={item} />
        ))}
      </div>
    </button>
  );
}


function SettingsToggle({ checked, onChange, label }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      onClick={() => onChange(!checked)}
      className="toggle-switch shrink-0 focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-gray-900"
      style={{
        '--toggle-bg': checked ? 'rgb(59 130 246)' : 'rgb(156 163 175)',
        '--toggle-height': '28px',
        '--toggle-width': '48px',
        '--knob-x': checked ? '22px' : '2px',
      }}
    >
      <span className="toggle-switch-knob" />
    </button>
  );
}


function RiskSettingsModal({
  open,
  settings,
  defaults,
  saving,
  onChange,
  onClose,
  onSave,
}) {
  if (!settings) return null;

  const setRiskType = (code, enabled) => {
    onChange({
      ...settings,
      risk_types: {
        ...settings.risk_types,
        [code]: enabled,
      },
    });
  };

  const setStage = (stageCode, patch) => {
    onChange({
      ...settings,
      stages: {
        ...settings.stages,
        [stageCode]: {
          ...settings.stages?.[stageCode],
          ...patch,
        },
      },
    });
  };

  const setNumericField = (field, rawValue) => {
    onChange({
      ...settings,
      [field]: rawValue === '' ? '' : Number(rawValue),
    });
  };

  const positiveInteger = (value, max) => (
    Number.isInteger(Number(value)) && Number(value) >= 1 && Number(value) <= max
  );
  const hasInvalidValue = (
    !positiveInteger(settings.order_age_days, 365)
    || !positiveInteger(settings.revision_age_days, 365)
    || RISK_STAGE_CODES.some((code) => (
      !positiveInteger(settings.stages?.[code]?.stale_after_hours, 8760)
    ))
  );
  const stageStaleEnabled = settings.risk_types?.stage_stale !== false;
  const orderOldEnabled = settings.risk_types?.order_old !== false;

  return (
    <ModalShell
      open={open}
      onClose={saving ? () => {} : onClose}
      variant="window"
      zIndexClass="z-[80]"
      panelClassName="!max-h-[88dvh] !max-w-lg !p-0 flex min-h-0 flex-col overflow-hidden"
      panelProps={{
        role: 'dialog',
        'aria-modal': true,
        'aria-labelledby': 'risk-settings-title',
      }}
    >
      <div className="flex shrink-0 items-start justify-between gap-3 border-b border-gray-200 px-4 py-3 dark:border-gray-800">
        <div className="min-w-0">
          <h2 id="risk-settings-title" className="text-base font-semibold text-gray-950 dark:text-white">
            Настройка рисков
          </h2>
          <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
            Правила применяются только к вашему аккаунту.
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          disabled={saving}
          className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-gray-500 transition hover:bg-gray-100 disabled:opacity-50 dark:hover:bg-gray-800"
          aria-label="Закрыть настройки"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-4 py-4">
        <section>
          <h3 className="text-xs font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400">
            Какие риски показывать
          </h3>
          <div className="mt-2 divide-y divide-gray-100 overflow-hidden rounded-xl border border-gray-200 bg-white dark:divide-gray-800 dark:border-gray-800 dark:bg-gray-900">
            {RISK_FILTERS.slice(1).map(({ code, label, icon: Icon }) => {
              const enabled = settings.risk_types?.[code] !== false;
              return (
                <div key={code} className="flex items-center gap-3 px-3 py-2.5">
                  <Icon className="h-4 w-4 shrink-0 text-gray-500 dark:text-gray-400" />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{label}</div>
                    <div className="text-[11px] leading-4 text-gray-500 dark:text-gray-400">
                      {RISK_DESCRIPTIONS[code]}
                    </div>
                  </div>
                  <SettingsToggle
                    checked={enabled}
                    onChange={(nextValue) => setRiskType(code, nextValue)}
                    label={`${enabled ? 'Отключить' : 'Включить'} риск «${label}»`}
                  />
                </div>
              );
            })}
          </div>
        </section>

        <section>
          <h3 className="text-xs font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400">
            Этапы и сроки
          </h3>
          <p className="mt-1 text-[11px] leading-4 text-gray-500 dark:text-gray-400">
            Отключённый этап полностью исчезнет из вашего контроля рисков.
          </p>
          <div className="mt-2 space-y-2">
            {RISK_STAGE_CODES.map((stageCode) => {
              const stageConfig = STAGES_CONFIG[stageCode] || { label: stageCode, icon: Clock3 };
              const StageIcon = stageConfig.icon || Clock3;
              const stage = settings.stages?.[stageCode] || { enabled: true, stale_after_hours: 72 };
              return (
                <div
                  key={stageCode}
                  className="flex min-w-0 items-center gap-2 rounded-xl border border-gray-200 bg-white px-3 py-2.5 dark:border-gray-800 dark:bg-gray-900"
                >
                  <StageIcon className="h-4 w-4 shrink-0 text-gray-500 dark:text-gray-400" />
                  <span className="min-w-0 flex-1 truncate text-sm font-medium text-gray-900 dark:text-gray-100">
                    {stageConfig.label}
                  </span>
                  <label className="flex shrink-0 items-center gap-1.5 text-[11px] text-gray-500 dark:text-gray-400">
                    <input
                      type="number"
                      min="1"
                      max="8760"
                      step="1"
                      value={stage.stale_after_hours}
                      onChange={(event) => setStage(stageCode, {
                        stale_after_hours: event.target.value === '' ? '' : Number(event.target.value),
                      })}
                      disabled={!stage.enabled || !stageStaleEnabled}
                      className="h-8 w-16 rounded-lg border border-gray-200 bg-white px-2 text-right text-sm text-gray-900 outline-none focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-400 dark:border-gray-700 dark:bg-gray-950 dark:text-gray-100 dark:disabled:bg-gray-800"
                      aria-label={`Срок риска этапа «${stageConfig.label}» в часах`}
                    />
                    ч
                  </label>
                  <SettingsToggle
                    checked={stage.enabled !== false}
                    onChange={(nextValue) => setStage(stageCode, { enabled: nextValue })}
                    label={`${stage.enabled !== false ? 'Скрыть' : 'Показывать'} этап «${stageConfig.label}»`}
                  />
                </div>
              );
            })}
          </div>
        </section>

        <section>
          <h3 className="text-xs font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400">
            Общий срок
          </h3>
          <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
            <label className="rounded-xl border border-gray-200 bg-white p-3 dark:border-gray-800 dark:bg-gray-900">
              <span className="block text-xs font-medium text-gray-700 dark:text-gray-300">Обычный заказ</span>
              <span className="mt-2 flex items-center gap-2">
                <input
                  type="number"
                  min="1"
                  max="365"
                  step="1"
                  value={settings.order_age_days}
                  onChange={(event) => setNumericField('order_age_days', event.target.value)}
                  disabled={!orderOldEnabled}
                  className="h-9 min-w-0 flex-1 rounded-lg border border-gray-200 bg-white px-2 text-right text-sm text-gray-900 outline-none focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-400 dark:border-gray-700 dark:bg-gray-950 dark:text-gray-100 dark:disabled:bg-gray-800"
                />
                <span className="text-xs text-gray-500 dark:text-gray-400">дней</span>
              </span>
            </label>
            <label className="rounded-xl border border-gray-200 bg-white p-3 dark:border-gray-800 dark:bg-gray-900">
              <span className="block text-xs font-medium text-gray-700 dark:text-gray-300">Перестирка</span>
              <span className="mt-2 flex items-center gap-2">
                <input
                  type="number"
                  min="1"
                  max="365"
                  step="1"
                  value={settings.revision_age_days}
                  onChange={(event) => setNumericField('revision_age_days', event.target.value)}
                  disabled={!orderOldEnabled}
                  className="h-9 min-w-0 flex-1 rounded-lg border border-gray-200 bg-white px-2 text-right text-sm text-gray-900 outline-none focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-400 dark:border-gray-700 dark:bg-gray-950 dark:text-gray-100 dark:disabled:bg-gray-800"
                />
                <span className="text-xs text-gray-500 dark:text-gray-400">дней</span>
              </span>
            </label>
          </div>
        </section>

        {hasInvalidValue ? (
          <p className="rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700 dark:bg-red-950/30 dark:text-red-300">
            Укажите целые значения: этапы — от 1 до 8760 часов, заказы — от 1 до 365 дней.
          </p>
        ) : null}
      </div>

      <div className="flex shrink-0 items-center justify-between gap-2 border-t border-gray-200 px-4 py-3 dark:border-gray-800">
        <button
          type="button"
          onClick={() => onChange(cloneRiskSettings(defaults))}
          disabled={saving || !defaults}
          className="h-9 rounded-lg px-2.5 text-xs font-medium text-gray-600 transition hover:bg-gray-100 disabled:opacity-50 dark:text-gray-300 dark:hover:bg-gray-800"
        >
          По умолчанию
        </button>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="h-9 rounded-lg border border-gray-200 px-3 text-xs font-medium text-gray-700 transition hover:bg-gray-50 disabled:opacity-50 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800"
          >
            Отмена
          </button>
          <button
            type="button"
            onClick={onSave}
            disabled={saving || hasInvalidValue}
            className="inline-flex h-9 items-center justify-center rounded-lg bg-blue-600 px-4 text-xs font-semibold text-white transition hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? 'Сохранение…' : 'Сохранить'}
          </button>
        </div>
      </div>
    </ModalShell>
  );
}


function RiskControlSkeleton() {
  return (
    <div className="space-y-3 px-4 py-3" aria-busy="true" aria-label="Загрузка контроля рисков">
      <div className="flex animate-pulse items-center justify-between gap-3">
        <div className="h-3 w-64 max-w-[72%] rounded-full bg-gray-200 dark:bg-gray-800" />
        <div className="h-8 w-9 shrink-0 rounded-lg border border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-900" />
      </div>

      <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
        {[0, 1, 2, 3].map((item) => (
          <div
            key={item}
            className="animate-pulse rounded-xl border border-gray-200 bg-white px-2.5 py-2.5 dark:border-gray-800 dark:bg-gray-900"
          >
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0 flex-1">
                <div className="h-2.5 w-20 rounded-full bg-gray-200 dark:bg-gray-800" />
                <div className="mt-2 h-5 w-10 rounded-full bg-gray-200 dark:bg-gray-700" />
              </div>
              <div className="h-4 w-4 shrink-0 rounded bg-gray-200 dark:bg-gray-700" />
            </div>
          </div>
        ))}
      </div>

      <div className="-mx-4 overflow-hidden px-4 pb-1">
        <div className="flex min-w-max animate-pulse gap-2">
          {[96, 138, 116, 102, 112].map((width, index) => (
            <div
              key={`${width}-${index}`}
              className="h-9 rounded-xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-900"
              style={{ width }}
            />
          ))}
        </div>
      </div>

      <div className="space-y-2">
        {[0, 1, 2, 3].map((item) => (
          <div
            key={item}
            className="animate-pulse rounded-xl border border-gray-200 bg-white p-2.5 shadow-sm dark:border-gray-800 dark:bg-gray-900"
          >
            <div className="flex items-center gap-2">
              <div className="h-6 w-20 shrink-0 rounded-md bg-gray-200 dark:bg-gray-700" />
              <div className="h-4 w-2/5 rounded-full bg-gray-200 dark:bg-gray-700" />
              <div className="ml-auto h-4 w-4 shrink-0 rounded bg-gray-200 dark:bg-gray-700" />
            </div>
            <div className="mt-2 flex gap-2">
              <div className="h-3 w-24 rounded-full bg-gray-100 dark:bg-gray-800" />
              <div className="h-3 w-32 rounded-full bg-gray-100 dark:bg-gray-800" />
              <div className="hidden h-3 w-28 rounded-full bg-gray-100 sm:block dark:bg-gray-800" />
            </div>
            <div className="mt-2 flex gap-1">
              <div className="h-6 w-24 rounded-md bg-gray-100 dark:bg-gray-800" />
              <div className="h-6 w-20 rounded-md bg-gray-100 dark:bg-gray-800" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function TodayDashboard() {
  const [section, setSection] = useState(() => sessionStorage.getItem('risk_control_section') || 'risks');
  return <>
    <div className="flex gap-2 px-4 pt-3" role="tablist" aria-label="Контроль рисков">
      {[['risks', 'Риски'], ['production', 'Загрузка и прогноз']].map(([key, label]) => <button key={key} type="button" role="tab" aria-selected={section === key} onClick={() => { setSection(key); sessionStorage.setItem('risk_control_section', key); }} className={`rounded-xl px-4 py-2.5 text-sm font-semibold ${section === key ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300'}`}>{label}</button>)}
    </div>
    {section === 'production' ? <ProductionDashboard /> : <RiskDashboard />}
  </>;
}

function RiskDashboard() {
  const navigate = useNavigate();
  const { toast, showToast, hideToast } = useToast(3200);
  const [summary, setSummary] = useState(null);
  const [riskSettings, setRiskSettings] = useState(null);
  const [defaultRiskSettings, setDefaultRiskSettings] = useState(null);
  const [riskSettingsAreCustom, setRiskSettingsAreCustom] = useState(false);
  const [showRiskSettings, setShowRiskSettings] = useState(false);
  const [draftRiskSettings, setDraftRiskSettings] = useState(null);
  const [savingRiskSettings, setSavingRiskSettings] = useState(false);
  const [activeFilter, setActiveFilter] = useState(readSavedRiskFilter);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');

  const loadData = useCallback(async ({ silent = false } = {}) => {
    if (silent) setRefreshing(true);
    else setLoading(true);
    setError('');

    try {
      const [data, settingsData] = await Promise.all([
        safeFetch(`${API_URL}/api/orders/risks`),
        safeFetch(`${API_URL}/api/orders/risk-settings`),
      ]);
      setSummary(data && Array.isArray(data.items) ? data : { items: [] });
      setRiskSettings(settingsData?.settings || null);
      setDefaultRiskSettings(settingsData?.defaults || null);
      setRiskSettingsAreCustom(Boolean(settingsData?.is_custom));
    } catch (loadError) {
      const message = loadError?.message || 'Не удалось загрузить контроль рисков';
      setError(message);
      showToast(message, 'error');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [showToast]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    try {
      sessionStorage.setItem(RISK_FILTER_STORAGE_KEY, activeFilter);
    } catch {
      // no-op
    }
  }, [activeFilter]);

  useEffect(() => {
    if (
      activeFilter !== 'all'
      && riskSettings?.risk_types?.[activeFilter] === false
    ) {
      setActiveFilter('all');
    }
  }, [activeFilter, riskSettings]);

  const items = useMemo(() => summary?.items || [], [summary]);

  const counts = useMemo(() => {
    const result = { all: items.length };
    RISK_FILTERS.slice(1).forEach(({ code }) => {
      result[code] = items.filter((item) => hasReason(item, code)).length;
    });
    return result;
  }, [items]);

  const availableRiskFilters = useMemo(() => (
    RISK_FILTERS.filter(({ code }) => (
      code === 'all' || riskSettings?.risk_types?.[code] !== false
    ))
  ), [riskSettings]);

  const visibleItems = useMemo(() => (
    activeFilter === 'all' ? items : items.filter((item) => hasReason(item, activeFilter))
  ), [activeFilter, items]);

  const criticalCount = items.filter((item) => item.severity === 'critical').length;
  const repeatCount = items.filter((item) => (
    hasReason(item, 'active_rework') || hasReason(item, 'active_revision')
  )).length;

  const openOrder = useCallback((item) => {
    haptic('tap');
    navigate(`/order/${item.order_id}?from=risk-control`);
  }, [navigate]);

  const selectFilter = (code) => {
    haptic('selection');
    setActiveFilter(code);
  };

  const openRiskSettings = () => {
    haptic('tap');
    setDraftRiskSettings(cloneRiskSettings(riskSettings || defaultRiskSettings));
    setShowRiskSettings(true);
  };

  const closeRiskSettings = () => {
    if (savingRiskSettings) return;
    setShowRiskSettings(false);
    setDraftRiskSettings(null);
  };

  useModalBackButton(showRiskSettings, closeRiskSettings);

  const saveRiskSettings = async () => {
    if (!draftRiskSettings || savingRiskSettings) return;
    setSavingRiskSettings(true);
    try {
      const response = await safeFetch(`${API_URL}/api/orders/risk-settings`, {
        method: 'PUT',
        body: JSON.stringify(draftRiskSettings),
      });
      setRiskSettings(response?.settings || draftRiskSettings);
      setDefaultRiskSettings(response?.defaults || defaultRiskSettings);
      setRiskSettingsAreCustom(Boolean(response?.is_custom));
      setShowRiskSettings(false);
      setDraftRiskSettings(null);
      showToast('Настройки рисков сохранены', 'success');
      await loadData({ silent: true });
    } catch (saveError) {
      showToast(saveError?.message || 'Не удалось сохранить настройки рисков', 'error');
    } finally {
      setSavingRiskSettings(false);
    }
  };

  if (loading) {
    return <RiskControlSkeleton />;
  }

  return (
    <div className="space-y-3 px-4 py-3 desktop-content-container-wide">
      <div className="flex items-center justify-between gap-3">
        {summary?.generated_at ? (
          <p className="min-w-0 truncate text-[11px] text-gray-400 dark:text-gray-500">
            Обновлено: {formatUpdatedAt(summary.generated_at)} · {riskSettingsAreCustom ? 'персональные правила' : 'стандартные правила'}
          </p>
        ) : <span />}
        <div className="flex shrink-0 items-center gap-1.5">
          <button
            type="button"
            onClick={openRiskSettings}
            disabled={!riskSettings}
            className="inline-flex h-8 shrink-0 items-center justify-center gap-1.5 rounded-lg border border-gray-200 bg-white px-2.5 text-xs font-medium text-gray-700 transition hover:bg-gray-50 disabled:opacity-60 dark:border-gray-800 dark:bg-gray-900 dark:text-gray-200 dark:hover:bg-gray-800"
          >
            <Settings2 className="h-4 w-4" />
            <span className="hidden sm:inline">Настроить</span>
          </button>
          <button
            type="button"
            onClick={() => loadData({ silent: true })}
            disabled={refreshing}
            className="inline-flex h-8 shrink-0 items-center justify-center gap-1.5 rounded-lg border border-gray-200 bg-white px-2.5 text-xs font-medium text-gray-700 transition hover:bg-gray-50 disabled:opacity-60 dark:border-gray-800 dark:bg-gray-900 dark:text-gray-200 dark:hover:bg-gray-800"
          >
            <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
            <span className="hidden sm:inline">Обновить</span>
          </button>
        </div>
      </div>

      {error ? (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900/60 dark:bg-red-950/25 dark:text-red-300">
          {error}
        </div>
      ) : null}

      <div className="grid grid-cols-2 gap-2 md:grid-cols-5">
        <MetricCard icon={ShieldAlert} label="Всего" value={items.length} />
        <MetricCard icon={AlertTriangle} label="Критичные" value={criticalCount} tone="danger" />
        {riskSettings?.risk_types?.deferred !== false ? (
          <MetricCard icon={Clock3} label="Отложенные" value={counts.deferred || 0} tone="orange" />
        ) : null}
        {riskSettings?.risk_types?.today_unassigned !== false ? (
          <MetricCard icon={UserRoundX} label="Без исполнителя" value={counts.today_unassigned || 0} tone="orange" />
        ) : null}
        {(
          riskSettings?.risk_types?.active_rework !== false
          || riskSettings?.risk_types?.active_revision !== false
        ) ? (
          <MetricCard icon={RefreshCw} label="Повторы" value={repeatCount} tone="blue" />
        ) : null}
      </div>

      <div className="-mx-4 overflow-x-auto px-4 pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        <div className="flex min-w-max gap-2">
          {availableRiskFilters.map(({ code, label, icon: Icon }) => {
            const selected = activeFilter === code;
            return (
              <button
                key={code}
                type="button"
                onClick={() => selectFilter(code)}
                className={`inline-flex h-9 items-center gap-1.5 rounded-xl border px-3 text-xs font-semibold transition ${selected
                  ? 'border-blue-600 bg-blue-600 text-white shadow-sm'
                  : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50 dark:border-gray-800 dark:bg-gray-900 dark:text-gray-300 dark:hover:bg-gray-800'}`}
              >
                <Icon className="h-3.5 w-3.5" />
                {label}
                <span className={`rounded-md px-1.5 py-0.5 text-[10px] ${selected ? 'bg-white/20' : 'bg-gray-100 dark:bg-gray-800'}`}>
                  {counts[code] || 0}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="space-y-2">
        {visibleItems.map((item) => (
          <RiskOrderCard key={item.order_id} item={item} onOpen={openOrder} />
        ))}

        {visibleItems.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-gray-300 bg-white px-4 py-10 text-center dark:border-gray-700 dark:bg-gray-900">
            <CheckCircle2 className="mx-auto h-9 w-9 text-emerald-500" />
            <div className="mt-3 text-sm font-semibold text-gray-900 dark:text-gray-100">
              {activeFilter === 'all' ? 'Активных рисков нет' : 'В этой категории заказов нет'}
            </div>
            <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Список обновится при следующей проверке.
            </div>
          </div>
        ) : null}
      </div>

      <RiskSettingsModal
        open={showRiskSettings}
        settings={draftRiskSettings}
        defaults={defaultRiskSettings}
        saving={savingRiskSettings}
        onChange={setDraftRiskSettings}
        onClose={closeRiskSettings}
        onSave={saveRiskSettings}
      />

          </div>
  );
}
